import moment from 'moment';

import { playerPaymentProfileStatus } from '@/helpers/ProfileHelpers';
import { formatNombreEnMonetaire } from '@/helpers/StringHelpers';

const getDefaultState = () => {
  return {
    montant: 0,
    montantFormatte: null, // Sert seulement pour l'affichage

    messages: {
      amount: {
        title: '',
        details: '',
      },
      voucher: {
        title: '',
        details: '',
      },
      billPayment: {
        title: '',
        details: '',
      },
    },
    showResponse: {
      forCredit: false,
      forVoucher: false,
      forInterac: false,
      forBillPayment: false,
    },
  };
};

// Valeurs utilisées tant que depositInfo n'est pas chargé (profil non chargé à l'ouverture de la page)
const DEPOT_MINIMUM_DEFAUT = 20;
const DEPOT_MAXIMUM_DEFAUT = 9999;

const state = getDefaultState();

const getters = {
  // depositInfo et profileDetails sont stockés dans le store "profile"
  depositInfo: (_state, _getters, rootState) => {
    return rootState.profile?.profileDetails?.depositInfo;
  },
  profileDetails: (_state, _getters, rootState) => {
    return rootState.profile?.esPayment?.profileDetails;
  },
  esPayment: (_state, _getters, rootState) => {
    return rootState.profile?.esPayment;
  },
  hasDepositLimit: (_state, getters) => {
    return getters.depositInfo?.hasDepositLimit;
  },
  depotMinimum: (_state, getters) => {
    return getters.depositInfo?.cardMinimumAmount ?? DEPOT_MINIMUM_DEFAUT;
  },
  depotMaximum: (_state, getters) => {
    return (
      getters.depositInfo?.maximumDepositAmount ??
      (Number(process.env.VUE_APP_MAX_LIMITE_DEPOT_HEBDO) || DEPOT_MAXIMUM_DEFAUT)
    );
  },

  webcashWeeklyLimitAmount: (_state, getters) => {
    if (
      !getters.depositInfo?.webcashPayMethodId ||
      !getters.depositInfo?.webcashWeeklyLimitAmount
    ) {
      return 500;
    }

    return getters.depositInfo?.webcashWeeklyLimitAmount;
  },
  billPaymentAccountNumber: (_state, getters) => {
    return getters.depositInfo?.billPaymentAccountNumber;
  },
  etStatus: (_state, getters) => {
    return getters.depositInfo?.etStatus;
  },
  esStatus: (_state, getters) => {
    return getters.depositInfo?.esStatus;
  },

  // Dépôt bloqué
  isDepotBloque: (_state, getters, _rootState, rootGetters) => {
    return (
      getters.depositInfo?.isBlocked ||
      rootGetters['profile/hasStatus'](playerPaymentProfileStatus.DEPOSIT_BLOCKED)
    );
  },
  // Dépôt non autorisé
  isUnauthorized: (_state, getters) => {
    return (
      !getters.depositInfo ||
      !getters.depositInfo.cardPayMethodId ||
      getters.depositInfo.cardPayMethodId === 'undefined' ||
      !getters.depositInfo.cardAllowDeposit ||
      (getters.depositInfo.webcashPayMethodId && !getters.depositInfo.webcashAllowDeposit)
    );
  },

  // Nombre d'essais
  isCreditCardAttemptsReached: (_state, getters) => {
    const today = new Date().toISOString();
    return (
      getters.depositInfo &&
      getters.depositInfo.creditCardFailedAttempts >=
        getters.depositInfo.creditCardFailedAttemptsThreshold &&
      moment(getters.depositInfo.creditCardLockDate).isAfter(today)
    );
  },
  isWebCashAttemptsReached: (_state, getters) => {
    const today = moment().format('YYYY-MM-DD');
    return (
      getters.depositInfo &&
      moment(getters.depositInfo.webcashLastAttemptDate).isSame(today) &&
      getters.depositInfo.webcashFailedAttempts >=
        getters.depositInfo.webcashFailedAttemptsThreshold
    );
  },

  // Limite hebdomadaire dépassée
  isDepositLimitReached: (_state, getters) => {
    return getters.depotMaximum === 0 || getters?.depotMaximum < getters?.depotMinimum;
  },
  iswebcashWeeklyLimitAmountReached: (_state, getters) => {
    return (
      getters.depositInfo?.webcashPayMethodId && getters.depositInfo.webcashMaximumAmount === 0
    );
  },
};

const actions = {};

const mutations = {
  RESET(state) {
    // Merge rather than replace so we don't lose observers
    // https://github.com/vuejs/vuex/issues/1118
    Object.assign(state, getDefaultState());
  },
  SET_DEPOT_MONTANT(state, value) {
    state.montant = value;
    state.montantFormatte = formatNombreEnMonetaire(value, 2);
  },
  SET_DEPOSIT_INFO(_state, depositInfo) {
    // depositInfo est stocké dans le store "profile"
    this.commit('profile/SET_DEPOSIT_INFO', depositInfo);
  },
  SET_ES_PAYMENT_REF(_state, paymentRef) {
    // paymentRef est stocké dans le store "profile"
    this.commit('profile/SET_ES_PAYMENT_REF', paymentRef);
  },
  SET_LQ_EMAIL(_state, lqEmail) {
    // lqEmail est stocké dans le store "profile"
    this.commit('profile/SET_LQ_EMAIL', lqEmail);
  },
  SET_MONTANT_ES(_state, montant) {
    // montantEsFormat est stocké dans le store "profile"
    this.commit('profile/SET_MONTANT_ES', montant);
  },
  SET_PAYMENTHANDLE_ID(_state, id) {
    this.commit('profile/SET_PAYMENTHANDLE_ID', id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
